import { useCallback, useEffect, useState } from 'react';

const getContainerPadding = (container: HTMLDivElement) => {
	const styles = window.getComputedStyle(container);
	return parseInt(styles.paddingLeft, 10) || 0;
};

const getAllCards = (container: HTMLDivElement): HTMLElement[] => {
	const cards = container.querySelectorAll<HTMLElement>(
		'[data-testid^="media-hit-"], [data-testid="media-hit-loading"]',
	);
	if (cards.length > 0) return Array.from(cards);
	return Array.from(container.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
};

const getCardPosition = (card: HTMLElement, container: HTMLDivElement): number => {
	const containerRect = container.getBoundingClientRect();
	const cardRect = card.getBoundingClientRect();
	return cardRect.left - containerRect.left;
};

const findNextCard = (cards: HTMLElement[], container: HTMLDivElement, paddingLeft: number): HTMLElement | null => {
	let nextCard: HTMLElement | null = null;
	let minDistance = Infinity;

	for (const card of cards) {
		const position = getCardPosition(card, container);
		if (position > paddingLeft) {
			const distance = position - paddingLeft;
			if (distance < minDistance) {
				minDistance = distance;
				nextCard = card;
			}
		}
	}

	return nextCard;
};

const findPreviousCard = (cards: HTMLElement[], container: HTMLDivElement, paddingLeft: number): HTMLElement | null => {
	let previousCard: HTMLElement | null = null;
	let maxPosition = -Infinity;

	for (const card of cards) {
		const position = getCardPosition(card, container);
		if (position < paddingLeft && position > maxPosition) {
			maxPosition = position;
			previousCard = card;
		}
	}

	return previousCard;
};

const calculateScrollDistance = (container: HTMLDivElement, direction: 'left' | 'right'): number => {
	const cards = getAllCards(container);
	if (cards.length === 0) return 0;

	const paddingLeft = getContainerPadding(container);

	if (direction === 'right') {
		const nextCard = findNextCard(cards, container, paddingLeft);
		if (nextCard) {
			return getCardPosition(nextCard, container) - paddingLeft;
		}
		const maxScroll = container.scrollWidth - container.clientWidth;
		return maxScroll - container.scrollLeft;
	}

	const previousCard = findPreviousCard(cards, container, paddingLeft);
	if (previousCard) {
		return getCardPosition(previousCard, container) - paddingLeft;
	}
	return -container.scrollLeft;
};

const calculateButtonStates = (container: HTMLDivElement) => {
	const hasOverflow = container.scrollWidth > container.clientWidth;

	if (!hasOverflow) {
		return { canScrollLeft: false, canScrollRight: false, hasOverflow: false };
	}

	const { scrollLeft, scrollWidth, clientWidth } = container;
	const maxScroll = scrollWidth - clientWidth;

	return {
		canScrollLeft: scrollLeft > 0.5,
		canScrollRight: scrollLeft < maxScroll - 0.5,
		hasOverflow: true,
	};
};

const setupScrollEndListener = (container: HTMLDivElement, onComplete: () => void) => {
	if ('onscrollend' in window) {
		const handleScrollEnd = () => {
			container.removeEventListener('scrollend', handleScrollEnd);
			onComplete();
		};
		container.addEventListener('scrollend', handleScrollEnd, { passive: true });
		return;
	}

	let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
	const handleScroll = () => {
		if (scrollTimeout) clearTimeout(scrollTimeout);
		scrollTimeout = setTimeout(() => {
			container.removeEventListener('scroll', handleScroll);
			onComplete();
		}, 150);
	};
	container.addEventListener('scroll', handleScroll, { passive: true });
};

const setupScrollObservers = (container: HTMLDivElement, updateScrollState: () => void): (() => void) => {
	requestAnimationFrame(updateScrollState);

	const mutationObserver = new MutationObserver(() => {
		setTimeout(updateScrollState, 50);
	});
	mutationObserver.observe(container, { childList: true, subtree: true });

	const resizeObserver = new ResizeObserver(() => {
		setTimeout(updateScrollState, 100);
	});
	resizeObserver.observe(container);

	let rafId: number | null = null;
	const handleScroll = () => {
		if (rafId !== null) return;
		rafId = requestAnimationFrame(() => {
			rafId = null;
			updateScrollState();
		});
	};
	container.addEventListener('scroll', handleScroll, { passive: true });

	const handleResize = () => {
		setTimeout(updateScrollState, 100);
	};
	window.addEventListener('resize', handleResize, { passive: true });

	return () => {
		mutationObserver.disconnect();
		resizeObserver.disconnect();
		container.removeEventListener('scroll', handleScroll);
		window.removeEventListener('resize', handleResize);
		if (rafId !== null) cancelAnimationFrame(rafId);
	};
};

const useScrollObservers = (
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	updateScrollState: () => void,
) => {
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (container) {
			return setupScrollObservers(container, updateScrollState);
		}

		let cleanup: (() => void) | undefined;
		const checkInterval = setInterval(() => {
			const currentContainer = scrollContainerRef.current;
			if (currentContainer) {
				clearInterval(checkInterval);
				cleanup = setupScrollObservers(currentContainer, updateScrollState);
			}
		}, 50);

		return () => {
			clearInterval(checkInterval);
			cleanup?.();
		};
	}, [scrollContainerRef, updateScrollState]);
};

type UseMediaHitScrollControlData = {
	canScrollLeft: boolean;
	canScrollRight: boolean;
	hasOverflow: boolean;
};

type UseMediaHitScrollControlActions = {
	updateScrollState: () => void;
	scrollByOneMediaHit: (direction: 'left' | 'right') => void;
};

type UseMediaHitScrollControlResult = ModelHookResult<UseMediaHitScrollControlData, UseMediaHitScrollControlActions>;

export function useMediaHitScrollControl(
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
): UseMediaHitScrollControlResult {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const states = calculateButtonStates(container);
		setCanScrollLeft(states.canScrollLeft);
		setCanScrollRight(states.canScrollRight);
		setHasOverflow(states.hasOverflow);
	}, [scrollContainerRef]);

	useScrollObservers(scrollContainerRef, updateScrollState);

	const scrollByOneMediaHit = useCallback(
		(direction: 'left' | 'right') => {
			const container = scrollContainerRef.current;
			if (!container) return;

			const distance = calculateScrollDistance(container, direction);
			const newPosition = container.scrollLeft + distance;
			const maxScroll = container.scrollWidth - container.clientWidth;
			const clampedPosition = Math.max(0, Math.min(newPosition, maxScroll));

			container.scrollTo({ left: clampedPosition, behavior: 'smooth' });
			setupScrollEndListener(container, updateScrollState);
		},
		[scrollContainerRef, updateScrollState],
	);

	return {
		data: { canScrollLeft, canScrollRight, hasOverflow },
		actions: { updateScrollState, scrollByOneMediaHit },
	};
}
